import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { ApiError, api } from '../api/client.ts';
import type {
  Billing, CheckInBody, Me, Person, PlanCreate, PlanSummary, Share, ShareDraft, VersionSummary, Waypoint, WirePlan,
} from '../api/types.ts';
import { t, type MessageKey } from '../i18n/index.ts';
import { withDatedEdits, withSetupDay, type CheckInResult, type MapChange } from '@map-my-money/views';
import { todayString } from '../lib/clock.ts';
import { setRegion } from '../lib/format.ts';
import { useToast } from './toast.tsx';
import { useUpgradePrompt } from '../components/UpgradePrompt.tsx';

export type AuthStatus = 'loading' | 'signedIn' | 'signedOut' | 'inviteOnly' | 'error';
export type PlanStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export interface Store {
  readonly auth: AuthStatus;
  readonly me: Me | null;
  /** Every plan the caller can open; empty on a server without plans or before the first save. */
  readonly plans: readonly PlanSummary[];
  /** null: the legacy routes, i.e. the caller's primary plan. */
  readonly activePlanId: string | null;
  readonly planStatus: PlanStatus;
  readonly plan: WirePlan | null;
  readonly version: number;
  readonly updatedAt: string | null;
  readonly versions: readonly VersionSummary[];
  readonly waypoints: readonly Waypoint[];
  readonly shares: readonly Share[];
  readonly drafts: readonly ShareDraft[];
  /** null until loaded, or on a server without billing. */
  readonly billing: Billing | null;
  readonly saving: boolean;
  /** Bumps whenever the plan is replaced from the server (409, restore, switch), so editors can reset. */
  readonly revision: number;
}

export interface Actions {
  loadSession: () => Promise<void>;
  reloadPlan: (options?: { quiet?: boolean }) => Promise<void>;
  refreshPlans: () => Promise<readonly PlanSummary[]>;
  switchPlan: (planId: string) => Promise<void>;
  createPlan: (create: PlanCreate) => Promise<PlanSummary>;
  renamePlan: (planId: string, name: string) => Promise<void>;
  deletePlan: (planId: string) => Promise<void>;
  /** A member leaves someone else's plan; if it was open, the caller's primary plan opens instead. */
  leavePlan: (planId: string) => Promise<void>;
  /** `changesFrom`: transaction id → the day its change takes effect (R24); a change left out counts from today. */
  savePlan: (next: WirePlan, summary: string, changesFrom?: Readonly<Record<string, string>>) => Promise<boolean>;
  /** `staleMessage` replaces the toast shown when the plan moved on before the restore landed. */
  restore: (version: number, staleMessage?: MessageKey) => Promise<boolean>;
  /** Saves a check-in; a refusal (R25) comes back as the server's messages, never as a toast. */
  checkIn: (body: CheckInBody) => Promise<CheckInOutcome>;
  refreshShares: () => Promise<void>;
  setShares: (update: (shares: readonly Share[]) => readonly Share[]) => void;
  refreshDrafts: () => Promise<void>;
  refreshBilling: () => Promise<Billing | null>;
  signOut: () => Promise<void>;
  signedOut: () => void;
}

export type CheckInOutcome =
  | { readonly saved: true; readonly result: CheckInResult | null; readonly changes?: readonly MapChange[] }
  | { readonly saved: false; readonly issues: readonly CheckInRefusal[] };

/** One reason the server refused a check-in, at the body path it is about (`/balances/cash`, `/debts/<id>`, `/date`). */
export interface CheckInRefusal {
  readonly path: string;
  readonly message: string;
}

const refusals = (error: ApiError): CheckInRefusal[] =>
  (error.body?.issues ?? []).flatMap((issue) => {
    const { path, message } = issue as { path?: unknown; message?: unknown };
    return typeof message === 'string' ? [{ path: typeof path === 'string' ? path : '', message }] : [];
  });

/** Provided by StoreProvider, or by the /try playground with a plan that lives only in the browser. */
export const StoreContext = createContext<(Store & Actions) | null>(null);

const INITIAL: Store = {
  auth: 'loading', me: null, plans: [], activePlanId: null, planStatus: 'idle', plan: null, version: 0, updatedAt: null,
  versions: [], waypoints: [], shares: [], drafts: [], billing: null, saving: false, revision: 0,
};

const ACTIVE_PLAN_KEY = 'mm.activePlan';

function rememberedPlan(): string | null {
  try {
    return localStorage.getItem(ACTIVE_PLAN_KEY);
  } catch {
    return null;
  }
}

export function rememberPlan(id: string | null) {
  try {
    if (id) localStorage.setItem(ACTIVE_PLAN_KEY, id);
    else localStorage.removeItem(ACTIVE_PLAN_KEY);
  } catch {
    /* storage may be unavailable */
  }
}

function withLock(plan: PlanSummary, locked: PlanSummary['locked']): PlanSummary {
  const { locked: _previous, ...rest } = plan;
  return locked ? { ...rest, locked } : rest;
}

/** The remembered plan if the caller can still open it, else the primary plan. */
function pickActive(plans: readonly PlanSummary[], wanted: string | null): string | null {
  if (wanted && plans.some((p) => p.id === wanted)) return wanted;
  return plans.find((p) => p.isPrimary && p.role === 'owner')?.id ?? null;
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<Store>(INITIAL);
  const toast = useToast();
  const promptUpgrade = useUpgradePrompt();
  const storeRef = useRef(store);
  storeRef.current = store;
  // Writes run one at a time so each carries the version the previous one produced.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  // Set synchronously on switch, so requests in flight for the previous plan can tell they are stale.
  const activeRef = useRef<string | null>(null);
  const patch = useCallback((p: Partial<Store> | ((s: Store) => Partial<Store>)) => {
    setStore((s) => {
      const next = { ...s, ...(typeof p === 'function' ? p(s) : p) };
      storeRef.current = next;
      return next;
    });
  }, []);

  const loadHistory = useCallback(async () => {
    const planId = activeRef.current;
    const [versions, waypoints] = await Promise.allSettled([api.versions(planId), api.waypoints(planId)]);
    if (activeRef.current !== planId) return;
    patch({
      ...(versions.status === 'fulfilled' ? { versions: versions.value.versions } : {}),
      ...(waypoints.status === 'fulfilled' ? { waypoints: waypoints.value.waypoints } : {}),
    });
  }, [patch]);

  const refreshShares = useCallback(async () => {
    try {
      const { shares } = await api.shares();
      patch({ shares });
    } catch {
      /* the list keeps its last value */
    }
  }, [patch]);

  const refreshDrafts = useCallback(async () => {
    try {
      const { drafts } = await api.shareDrafts();
      patch({ drafts });
    } catch {
      /* a server without drafts has none to confirm */
    }
  }, [patch]);

  const refreshBilling = useCallback(async () => {
    try {
      const billing = await api.billing();
      patch({ billing });
      return billing;
    } catch {
      return storeRef.current.billing;
    }
  }, [patch]);

  const refreshPlans = useCallback(async () => {
    try {
      const { plans } = await api.plans();
      patch({ plans });
      return plans;
    } catch {
      return storeRef.current.plans;
    }
  }, [patch]);

  /** `quiet`: refresh the document in place, as after a check-in that changed the map, so open dialogs stay. */
  const reloadPlan = useCallback(async (options: { quiet?: boolean } = {}) => {
    const planId = activeRef.current;
    if (!options.quiet) patch({ planStatus: 'loading' });
    try {
      let document: { plan: WirePlan | null; version: number; updatedAt: string | null };
      if (planId) {
        const detail = await api.plan(planId);
        document = { plan: detail.plan, version: detail.version, updatedAt: detail.updatedAt };
        patch((s) => ({ plans: s.plans.map((p) => (p.id === planId ? withLock({ ...p, name: detail.name, role: detail.role, version: detail.version }, detail.locked) : p)) }));
      } else {
        document = await api.getPlan();
      }
      if (activeRef.current !== planId) return;
      if (!document.plan) {
        patch((s) => ({ planStatus: 'empty', plan: null, version: document.version, updatedAt: document.updatedAt, versions: [], waypoints: [], revision: s.revision + 1 }));
        return;
      }
      const plan = document.plan;
      setRegion(plan.configuration.taxJurisdiction);
      patch((s) => ({ planStatus: 'ready', plan, version: document.version, updatedAt: document.updatedAt, revision: s.revision + 1 }));
    } catch (error) {
      if (activeRef.current !== planId) return;
      if (error instanceof ApiError && error.status === 404) patch({ planStatus: 'empty', plan: null, version: 0, versions: [], waypoints: [] });
      else if (error instanceof ApiError && error.status === 401) patch({ auth: 'signedOut', planStatus: 'idle' });
      else patch({ planStatus: 'error' });
      return;
    }
    await Promise.all([loadHistory(), refreshShares()]);
  }, [patch, loadHistory, refreshShares]);

  const activate = useCallback(
    async (planId: string | null) => {
      rememberPlan(planId);
      activeRef.current = planId;
      patch((s) => ({ activePlanId: planId, plan: null, version: 0, updatedAt: null, versions: [], waypoints: [], revision: s.revision + 1 }));
      await reloadPlan();
    },
    [patch, reloadPlan],
  );

  const loadSession = useCallback(async () => {
    try {
      // The plan list and drafts load alongside /me; they fail harmlessly when signed out.
      const lists = Promise.all([refreshPlans(), refreshDrafts()]);
      const me = await api.me();
      if (!me.access.cloud) {
        patch({ auth: 'inviteOnly', me });
        return;
      }
      patch({ auth: 'signedIn', me, planStatus: 'loading' });
      void refreshBilling();
      const [plans] = await lists;
      await activate(pickActive(plans, rememberedPlan()));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) patch({ auth: 'signedOut' });
      else if (error instanceof ApiError && error.status === 403) patch({ auth: 'inviteOnly' });
      else patch({ auth: 'error' });
    }
  }, [patch, refreshPlans, refreshDrafts, refreshBilling, activate]);

  const switchPlan = useCallback(
    async (planId: string) => {
      if (planId === activeRef.current && storeRef.current.planStatus === 'ready') return;
      await activate(planId);
    },
    [activate],
  );

  const createPlan = useCallback(
    async (create: PlanCreate) => {
      const plan = await api.createPlan(create);
      patch((s) => ({ plans: [...s.plans.filter((p) => p.role === 'owner'), plan, ...s.plans.filter((p) => p.role !== 'owner')] }));
      void refreshPlans();
      void refreshBilling();
      return plan;
    },
    [patch, refreshPlans, refreshBilling],
  );

  const renamePlan = useCallback(
    async (planId: string, name: string) => {
      const plan = await api.renamePlan(planId, name);
      patch((s) => ({ plans: s.plans.map((p) => (p.id === planId ? { ...p, ...plan } : p)) }));
    },
    [patch],
  );

  const deletePlan = useCallback(
    async (planId: string) => {
      await api.deletePlan(planId);
      const plans = storeRef.current.plans.filter((p) => p.id !== planId).map((p) => (p.parentPlanId === planId ? { ...p, parentPlanId: null } : p));
      patch({ plans });
      if (activeRef.current === planId) await activate(pickActive(plans, null));
      void refreshShares();
      // Deleting a plan can bring a read-only one back under the cap.
      void refreshPlans();
      void refreshBilling();
    },
    [patch, activate, refreshShares, refreshPlans, refreshBilling],
  );

  const leavePlan = useCallback(
    async (planId: string) => {
      await api.leavePlan(planId);
      const plans = storeRef.current.plans.filter((p) => p.id !== planId);
      patch({ plans });
      if (activeRef.current === planId) await activate(pickActive(plans, null));
      void refreshShares();
    },
    [patch, activate, refreshShares],
  );

  const reportWriteError = useCallback(
    (error: unknown) => {
      const upgrade = error instanceof ApiError ? error.upgrade : null;
      if (upgrade) {
        promptUpgrade(upgrade);
      } else if (error instanceof ApiError && error.isPlanTooLarge) {
        toast.push({ tone: 'error', message: error.message });
      } else if (error instanceof ApiError && error.status === 422) {
        toast.push({ tone: 'error', message: t('error.invalid', { detail: error.message }) });
      } else if (error instanceof ApiError && error.status === 403) {
        toast.push({ tone: 'error', message: t('plans.readOnly.toast') });
      } else if (error instanceof ApiError && error.status === 429) {
        toast.push({ tone: 'error', message: t('error.rateLimited') });
      } else {
        toast.push({ tone: 'error', message: t('error.saveFailed') });
      }
    },
    [toast, promptUpgrade],
  );

  const adoptServerPlan = useCallback(
    (error: ApiError, message: MessageKey = 'error.stale') => {
      const body = error.body;
      if (body?.plan && typeof body.version === 'number') {
        patch((s) => ({ plan: body.plan!, version: body.version!, revision: s.revision + 1 }));
      }
      toast.push({ tone: 'error', message: t(message) });
      void loadHistory();
    },
    [patch, toast, loadHistory],
  );

  /** True when the open plan can't be changed; says why (a calm upgrade prompt when it is a billing lock). */
  const blockedWrite = useCallback(() => {
    const plan = storeRef.current.plans.find((p) => p.id === activeRef.current);
    if (plan?.locked === 'lapsed' && plan.role !== 'owner') {
      toast.push({ tone: 'error', message: t('plans.locked.lapsedMember') });
      return true;
    }
    if (plan?.locked) {
      const tier = storeRef.current.billing?.tier ?? 'none';
      const feature = plan.locked === 'plan_limit' ? 'plan_read_only' : plan.locked === 'lapsed' ? 'lapsed' : 'editor_seat';
      promptUpgrade({ feature, limit: null, tier, upgradeUrl: '' });
      return true;
    }
    if (plan?.role === 'viewer') {
      toast.push({ tone: 'error', message: t('plans.readOnly.toast') });
      return true;
    }
    return false;
  }, [promptUpgrade, toast]);

  const savePlan = useCallback(
    (edited: WirePlan, summary: string, changesFrom?: Readonly<Record<string, string>>) => {
      const run = async (): Promise<boolean> => {
        if (blockedWrite()) return false;
        const before = storeRef.current;
        const planId = activeRef.current;
        const today = todayString();
        const next = withDatedEdits(withSetupDay(edited, before.plan, today), before.plan, today, changesFrom);
        patch({ plan: next, saving: true, ...(before.planStatus === 'empty' ? { planStatus: 'ready' as const } : {}) });
        try {
          const { version, updatedAt } = await api.putPlan({ plan: next, baseVersion: before.version, summary, ...(changesFrom ? { changesFrom } : {}) }, planId);
          patch((s) => ({ version, updatedAt, saving: false, plans: s.plans.map((p) => (p.id === planId ? { ...p, version, updatedAt } : p)) }));
          if (!planId) void refreshPlans().then((plans) => {
            const primary = pickActive(plans, null);
            if (primary && !activeRef.current) {
              rememberPlan(primary);
              activeRef.current = primary;
              patch({ activePlanId: primary });
            }
          });
          void loadHistory();
          return true;
        } catch (error) {
          patch({ saving: false });
          if (error instanceof ApiError && error.isStale) {
            adoptServerPlan(error);
          } else {
            patch((s) => ({ plan: before.plan, planStatus: before.planStatus, revision: s.revision + 1 }));
            reportWriteError(error);
          }
          return false;
        }
      };
      const result = queue.current.then(run, run);
      queue.current = result;
      return result;
    },
    [patch, loadHistory, adoptServerPlan, reportWriteError, blockedWrite, refreshPlans],
  );

  const restore = useCallback(
    async (version: number, staleMessage?: MessageKey) => {
      patch({ saving: true });
      try {
        await api.restore(version, storeRef.current.version, activeRef.current);
        patch({ saving: false });
        await reloadPlan();
        toast.push({ tone: 'success', message: t('connect.history.restored', { n: version }) });
        return true;
      } catch (error) {
        patch({ saving: false });
        if (error instanceof ApiError && error.isStale) adoptServerPlan(error, staleMessage);
        else reportWriteError(error);
        return false;
      }
    },
    [patch, reloadPlan, toast, adoptServerPlan, reportWriteError],
  );

  const checkIn = useCallback(
    async (body: CheckInBody): Promise<CheckInOutcome> => {
      try {
        const { waypoint, result, plan } = await api.checkIn(body, activeRef.current);
        patch((s) => ({ waypoints: [waypoint, ...s.waypoints].sort((a, b) => b.date.localeCompare(a.date)) }));
        if (plan) await reloadPlan({ quiet: true });
        return { saved: true, result: result ?? null, ...(plan ? { changes: plan.changes } : {}) };
      } catch (error) {
        const issues = error instanceof ApiError && error.status === 422 ? refusals(error) : [];
        if (issues.length === 0) reportWriteError(error);
        return { saved: false, issues };
      }
    },
    [patch, toast, reportWriteError, reloadPlan],
  );

  const setShares = useCallback((update: (shares: readonly Share[]) => readonly Share[]) => patch((s) => ({ shares: update(s.shares) })), [patch]);

  const signedOut = useCallback(() => {
    activeRef.current = null;
    setStore({ ...INITIAL, auth: 'signedOut' });
  }, []);
  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      signedOut();
    }
  }, [signedOut]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const value = useMemo(
    () => ({
      ...store, loadSession, reloadPlan, refreshPlans, switchPlan, createPlan, renamePlan, deletePlan, leavePlan, savePlan, restore, checkIn,
      refreshShares, setShares, refreshDrafts, refreshBilling, signOut, signedOut,
    }),
    [store, loadSession, reloadPlan, refreshPlans, switchPlan, createPlan, renamePlan, deletePlan, leavePlan, savePlan, restore, checkIn,
      refreshShares, setShares, refreshDrafts, refreshBilling, signOut, signedOut],
  );
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const store = useContext(StoreContext);
  if (!store) throw new Error('useStore outside StoreProvider');
  return store;
}

/** The plan once loaded; screens under the signed-in shell only render when it exists. */
export function usePlan(): WirePlan {
  const { plan } = useStore();
  if (!plan) throw new Error('usePlan before the plan loaded');
  return plan;
}

const nameFromEmail = (email: string | undefined): string | null => {
  const local = email?.split('@')[0]?.split(/[._+-]/)[0];
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : null;
};

export const ownerName = (plan: PlanSummary | null | undefined): string | null => nameFromEmail(plan?.ownerEmail);

/** What to call someone on a plan: their display name, else a first name guessed from their email. */
export const personName = (person: Person): string => person.displayName?.trim() || nameFromEmail(person.email) || person.email;

export interface ActivePlan {
  readonly plan: PlanSummary | null;
  /** False for a viewer and for a plan that is read-only for billing reasons. */
  readonly canEdit: boolean;
  readonly isOwner: boolean;
  /** Why editing is off, for a viewer (billing locks have their own banner). */
  readonly readOnlyReason: string | null;
  readonly multiple: boolean;
}

export function useActivePlan(): ActivePlan {
  const { plans, activePlanId } = useStore();
  return useMemo(() => {
    const plan = plans.find((p) => p.id === activePlanId) ?? null;
    const role = plan?.role ?? 'owner';
    const owner = ownerName(plan);
    return {
      plan,
      canEdit: role !== 'viewer' && !plan?.locked,
      isOwner: role === 'owner',
      readOnlyReason: role === 'viewer' ? (owner ? t('plans.readOnly.reason', { name: owner }) : t('plans.readOnly.reasonAnon')) : null,
      multiple: plans.length > 1,
    };
  }, [plans, activePlanId]);
}

/** How many plans of their own the caller may have; null = no limit. Today's 5 until billing says otherwise. */
export const LEGACY_PLAN_LIMIT = 5;
export function usePlanLimit(): number | null {
  const { billing } = useStore();
  return billing ? billing.limits.ownedPlans : LEGACY_PLAN_LIMIT;
}
